import {
    collection,
    doc,
    addDoc,
    getDoc,
    getDocs,
    updateDoc,
    deleteDoc,
    query,
    where
} from "firebase/firestore";
import { db, trackerDb } from "../config";
import { getAllArchetypes } from "./trackerArchetypes";

// --- Tracker Database - Decks ---

export const getUserDecks = async (userId) => {
    try {
        const decksRef = collection(trackerDb, "decks");
        const q = query(decksRef, where("author", "==", userId));
        const querySnapshot = await getDocs(q);

        const decks = querySnapshot.docs.map(deckDoc => ({
            id: deckDoc.id,
            ...deckDoc.data()
        }));

        return decks;
    } catch (error) {
        console.error("Błąd podczas pobierania talii użytkownika:", error);
        throw error;
    }
};

export const getPublicDecks = async () => {
    try {
        const decksRef = collection(trackerDb, "decks");
        const q = query(decksRef, where("privacy", "==", "public"));
        const querySnapshot = await getDocs(q);

        const decks = querySnapshot.docs.map(deckDoc => ({
            id: deckDoc.id,
            ...deckDoc.data()
        }));

        return decks;
    } catch (error) {
        console.error("Błąd podczas pobierania publicznych talii:", error);
        throw error;
    }
};

export const getInternalDecks = async () => {
    try {
        const decksRef = collection(trackerDb, "decks");
        const q = query(decksRef, where("privacy", "in", ["internal", "public"]));
        const querySnapshot = await getDocs(q);

        const decks = querySnapshot.docs.map(deckDoc => ({
            id: deckDoc.id,
            ...deckDoc.data()
        }));

        return decks;
    } catch (error) {
        console.error("Błąd podczas pobierania talii wewnętrznych:", error);
        throw error;
    }
};

export const getDeck = async (deckId) => {
    try {
        const deckRef = doc(trackerDb, "decks", deckId);
        const deckDoc = await getDoc(deckRef);

        if (deckDoc.exists()) {
            return { id: deckDoc.id, ...deckDoc.data() };
        } else {
            return null;
        }
    } catch (error) {
        console.error("Błąd podczas pobierania talii:", error);
        throw error;
    }
};

export const getDeckById = async (deckId) => {
    try {
        const deckRef = doc(trackerDb, "decks", deckId);
        const deckDoc = await getDoc(deckRef);

        if (!deckDoc.exists()) {
            return null;
        }

        const deck = { id: deckDoc.id, ...deckDoc.data() };

        if (deck.archetype) {
            const archetypeRef = doc(trackerDb, "archetype", deck.archetype);
            const archetypeDoc = await getDoc(archetypeRef);

            if (archetypeDoc.exists()) {
                deck.archetype = { id: archetypeDoc.id, ...archetypeDoc.data() };
            }
        }

        return deck;
    } catch (error) {
        console.error("Błąd podczas pobierania talii z archetypem:", error);
        throw error;
    }
};

export const createDeck = async (deckData) => {
    try {
        const decksRef = collection(trackerDb, "decks");
        const deckWithTimestamp = {
            ...deckData,
            createdAt: new Date().toISOString(),
            modifiedAt: new Date().toISOString()
        };
        const createdDocRef = await addDoc(decksRef, deckWithTimestamp);
        return createdDocRef.id;
    } catch (error) {
        console.error("Błąd podczas tworzenia talii:", error);
        throw error;
    }
};

export const updateDeck = async (deckId, deckData) => {
    try {
        const deckRef = doc(trackerDb, "decks", deckId);
        const deckWithTimestamp = {
            ...deckData,
            modifiedAt: new Date().toISOString()
        };
        await updateDoc(deckRef, deckWithTimestamp);
        return deckId;
    } catch (error) {
        console.error("Błąd podczas aktualizacji talii:", error);
        throw error;
    }
};

export const deleteDeck = async (deckId) => {
    try {
        const deckRef = doc(trackerDb, "decks", deckId);
        await deleteDoc(deckRef);
        return true;
    } catch (error) {
        console.error("Błąd podczas usuwania talii:", error);
        throw error;
    }
};

export const getDecksWithArchetypes = async (userId = null) => {
    try {
        let decks;

        if (userId) {
            decks = await getUserDecks(userId);
        } else {
            decks = await getInternalDecks();
        }

        const archetypes = await getAllArchetypes();
        const archetypeMap = {};
        archetypes.forEach(archetype => {
            archetypeMap[archetype.id] = archetype;
        });

        const decksWithArchetypes = decks.map(deck => ({
            ...deck,
            archetypeInfo: archetypeMap[deck.archetype] || null
        }));

        return decksWithArchetypes;
    } catch (error) {
        console.error("Błąd podczas pobierania talii z archetypami:", error);
        throw error;
    }
};

export const getDecksByArchetype = async (archetypeId, userId = null) => {
    try {
        let decks;
        if (userId) {
            const [userDecks, internalDecks] = await Promise.all([
                getUserDecks(userId),
                getInternalDecks()
            ]);
            const userDeckIds = new Set(userDecks.map(d => d.id));
            decks = [...userDecks];
            internalDecks.forEach(d => {
                if (!userDeckIds.has(d.id)) decks.push(d);
            });
        } else {
            decks = await getInternalDecks();
        }
        return decks.filter(d => d.archetype === archetypeId);
    } catch (error) {
        console.error("Błąd podczas pobierania talii archetypu:", error);
        throw error;
    }
};

// --- Tracker Database - Matches ---

export const createMatch = async (matchData) => {
    try {
        const matchesRef = collection(trackerDb, "matches");
        const matchWithTimestamp = {
            ...matchData,
            createdAt: new Date().toISOString(),
            modifiedAt: new Date().toISOString()
        };
        const createdDocRef = await addDoc(matchesRef, matchWithTimestamp);
        return createdDocRef.id;
    } catch (error) {
        console.error("Błąd podczas tworzenia meczu:", error);
        throw error;
    }
};

export const getUserMatches = async (userId) => {
    try {
        const matchesRef = collection(trackerDb, "matches");
        const q = query(matchesRef, where("userId", "==", userId));
        const querySnapshot = await getDocs(q);

        const matches = querySnapshot.docs.map(matchDoc => ({
            id: matchDoc.id,
            ...matchDoc.data()
        }));

        matches.sort((a, b) => {
            const timestampA = a.timestamp || a.createdAt || 0;
            const timestampB = b.timestamp || b.createdAt || 0;
            return timestampB - timestampA;
        });

        return matches;
    } catch (error) {
        console.error("Błąd podczas pobierania meczy użytkownika:", error);
        throw error;
    }
};

export const updateMatch = async (matchId, matchData) => {
    try {
        const matchRef = doc(trackerDb, "matches", matchId);
        const updateData = {
            ...matchData,
            modifiedAt: new Date().toISOString()
        };
        await updateDoc(matchRef, updateData);
        return true;
    } catch (error) {
        console.error("Błąd podczas aktualizacji meczu:", error);
        throw error;
    }
};

export const deleteMatch = async (matchId) => {
    try {
        const matchRef = doc(trackerDb, "matches", matchId);
        await deleteDoc(matchRef);
        return true;
    } catch (error) {
        console.error("Błąd podczas usuwania meczu:", error);
        throw error;
    }
};

export const deleteMatches = async (matchIds) => {
    try {
        const deletePromises = matchIds.map(matchId => deleteMatch(matchId));
        await Promise.all(deletePromises);
        return true;
    } catch (error) {
        console.error("Błąd podczas usuwania meczy:", error);
        throw error;
    }
};

export const getUserMatchesWithDetails = async (userId) => {
    try {
        const matches = await getUserMatches(userId);

        const [userDecks, internalDecks, archetypes] = await Promise.all([
            getUserDecks(userId),
            getInternalDecks(),
            getAllArchetypes()
        ]);

        const allDecks = [...userDecks];
        const userDeckIds = new Set(userDecks.map(deck => deck.id));

        internalDecks.forEach(internalDeck => {
            if (!userDeckIds.has(internalDeck.id)) {
                allDecks.push(internalDeck);
            }
        });

        const deckMap = {};
        allDecks.forEach(deck => {
            deckMap[deck.id] = deck;
        });

        const archetypeMap = {};
        archetypes.forEach(archetype => {
            archetypeMap[archetype.id] = archetype;
        });

        const matchesWithDetails = matches.map(match => ({
            ...match,
            myDeckInfo: deckMap[match.myDeckId] || null,
            opponentArchetypeInfo: archetypeMap[match.opponentArchetypeId] || null
        }));

        return matchesWithDetails;
    } catch (error) {
        console.error("Błąd podczas pobierania meczy z szczegółami:", error);
        throw error;
    }
};

export const updateUserLastSelectedDeck = async (userId, deckId) => {
    try {
        const userRef = doc(db, "users", userId);
        await updateDoc(userRef, {
            lastSelectedDeckId: deckId,
            modifiedAt: new Date().toISOString()
        });
        return userId;
    } catch (error) {
        console.error("Błąd podczas aktualizacji ostatnio wybranej talii:", error);
        throw error;
    }
};

export const updateUserLastSelectedArchetype = async (userId, archetypeId) => {
    try {
        const userRef = doc(db, "users", userId);
        await updateDoc(userRef, {
            lastSelectedArchetypeId: archetypeId,
            modifiedAt: new Date().toISOString()
        });
        return userId;
    } catch (error) {
        console.error("Błąd podczas aktualizacji ostatnio wybranego archetypu:", error);
        throw error;
    }
};

export const getAggregatedArchetypeStats = async (userId) => {
    try {
        const [userDecks, internalDecks] = await Promise.all([
            getUserDecks(userId),
            getInternalDecks()
        ]);

        const allDecks = [...userDecks];
        const userDeckIds = new Set(userDecks.map(deck => deck.id));

        internalDecks.forEach(internalDeck => {
            if (!userDeckIds.has(internalDeck.id)) {
                allDecks.push(internalDeck);
            }
        });

        const archetypes = await getAllArchetypes();
        const archetypeMap = {};
        archetypes.forEach(archetype => {
            archetypeMap[archetype.id] = archetype;
        });

        const aggregatedStats = {};

        allDecks.forEach(deck => {
            if (deck.stats && deck.stats.vsArchetypes && deck.archetype) {
                const archetypeId = deck.archetype;
                const archetypeInfo = archetypeMap[archetypeId];

                if (!aggregatedStats[archetypeId]) {
                    aggregatedStats[archetypeId] = {
                        name: archetypeInfo?.name || 'Nieznany archetyp',
                        colors: archetypeInfo?.ink_colors || [],
                        vsArchetypes: {},
                        perPlayer: {}
                    };
                }

                Object.entries(deck.stats.vsArchetypes).forEach(([opponentArchetypeId, stats]) => {
                    if (!aggregatedStats[archetypeId].vsArchetypes[opponentArchetypeId]) {
                        const opponentArchetypeInfo = archetypeMap[opponentArchetypeId];
                        aggregatedStats[archetypeId].vsArchetypes[opponentArchetypeId] = {
                            name: opponentArchetypeInfo?.name || 'Nieznany archetyp',
                            colors: opponentArchetypeInfo?.ink_colors || [],
                            games: 0,
                            wins: 0,
                            losses: 0,
                            winrate: 0,
                            play: { games: 0, wins: 0, losses: 0, winrate: 0 },
                            draw: { games: 0, wins: 0, losses: 0, winrate: 0 }
                        };
                    }

                    const current = aggregatedStats[archetypeId].vsArchetypes[opponentArchetypeId];
                    current.games += stats.games || 0;
                    current.wins += stats.wins || 0;
                    current.losses += stats.losses || 0;

                    if (stats.play) {
                        current.play.games += stats.play.games || 0;
                        current.play.wins += stats.play.wins || 0;
                        current.play.losses += stats.play.losses || 0;
                    }

                    if (stats.draw) {
                        current.draw.games += stats.draw.games || 0;
                        current.draw.wins += stats.draw.wins || 0;
                        current.draw.losses += stats.draw.losses || 0;
                    }
                });
            }

            if (deck.stats && deck.stats.perPlayer && deck.archetype) {
                const archetypeId = deck.archetype;

                if (!aggregatedStats[archetypeId]) {
                    const archetypeInfo = archetypeMap[archetypeId];
                    aggregatedStats[archetypeId] = {
                        name: archetypeInfo?.name || 'Nieznany archetyp',
                        colors: archetypeInfo?.ink_colors || [],
                        vsArchetypes: {},
                        perPlayer: {}
                    };
                }

                Object.entries(deck.stats.perPlayer).forEach(([playerId, playerStats]) => {
                    if (!aggregatedStats[archetypeId].perPlayer[playerId]) {
                        aggregatedStats[archetypeId].perPlayer[playerId] = {
                            name: playerStats.name || 'Nieznany gracz',
                            games: 0,
                            wins: 0,
                            losses: 0,
                            winrate: 0,
                            play: { games: 0, wins: 0, losses: 0, winrate: 0 },
                            draw: { games: 0, wins: 0, losses: 0, winrate: 0 }
                        };
                    }

                    const current = aggregatedStats[archetypeId].perPlayer[playerId];
                    current.games += playerStats.games || 0;
                    current.wins += playerStats.wins || 0;
                    current.losses += playerStats.losses || 0;

                    if (playerStats.play) {
                        current.play.games += playerStats.play.games || 0;
                        current.play.wins += playerStats.play.wins || 0;
                        current.play.losses += playerStats.play.losses || 0;
                    }

                    if (playerStats.draw) {
                        current.draw.games += playerStats.draw.games || 0;
                        current.draw.wins += playerStats.draw.wins || 0;
                        current.draw.losses += playerStats.draw.losses || 0;
                    }
                });
            }
        });

        Object.values(aggregatedStats).forEach(archetype => {
            Object.values(archetype.vsArchetypes).forEach(vsStats => {
                if (vsStats.games > 0) {
                    vsStats.winrate = Math.round((vsStats.wins / vsStats.games) * 100 * 100) / 100;
                }
                if (vsStats.play.games > 0) {
                    vsStats.play.winrate = Math.round((vsStats.play.wins / vsStats.play.games) * 100 * 100) / 100;
                }
                if (vsStats.draw.games > 0) {
                    vsStats.draw.winrate = Math.round((vsStats.draw.wins / vsStats.draw.games) * 100 * 100) / 100;
                }
            });

            Object.values(archetype.perPlayer).forEach(playerStats => {
                if (playerStats.games > 0) {
                    playerStats.winrate = Math.round((playerStats.wins / playerStats.games) * 100 * 100) / 100;
                }
                if (playerStats.play.games > 0) {
                    playerStats.play.winrate = Math.round((playerStats.play.wins / playerStats.play.games) * 100 * 100) / 100;
                }
                if (playerStats.draw.games > 0) {
                    playerStats.draw.winrate = Math.round((playerStats.draw.wins / playerStats.draw.games) * 100 * 100) / 100;
                }
            });
        });

        Object.entries(aggregatedStats).forEach(([, archetype]) => {
            const totalGames = Object.values(archetype.vsArchetypes).reduce((sum, vsStats) => sum + vsStats.games, 0);
            archetype.totalGames = totalGames;
        });

        const sortedEntries = Object.entries(aggregatedStats).sort(([, a], [, b]) => b.totalGames - a.totalGames);

        const sortedAggregatedStats = {};
        sortedEntries.forEach(([archetypeId, archetype]) => {
            sortedAggregatedStats[archetypeId] = archetype;
        });

        return sortedAggregatedStats;
    } catch (error) {
        console.error("Błąd podczas pobierania zagregowanych statystyk archetypów:", error);
        throw error;
    }
};
